import {
  ProviderUnavailableError,
  type ProviderCallOptions,
  type ProviderJobHandle,
  type ProviderVideoEnhanceJobState,
  type VideoEnhanceProvider,
  type VideoEnhanceRequest,
  type VideoEnhanceResult,
} from "@loumy/contracts";

// §7 `video-enhance`-Provider → **SeedVR2** (ByteDance) über die fal.ai-Queue-API (D168):
// temporal-konsistente Video-Super-Resolution/Detail-Rekonstruktion — Quell-Video rein,
// schärferes (hochskaliertes) Video raus. Gewinnt lt. 2026-Vergleichen gerade bei KURZEN,
// KI-GENERIERTEN, komprimierten Clips (genau die Pixverse-Spritesheet-Loops; FlashVSR wäre
// die Speed-Alternative für Real-Footage). Versorgt den optionalen `enhance-video`-Knoten
// der Spritesheet-Pipeline. Gleicher FAL_API_KEY wie die übrigen fal-Adapter.
// COGS: $0.001 je MPx Video-Daten (w × h × frames des OUTPUTS) — 2× von 540p ⇒ 1080p-Klasse:
// 2-s-Loop (49 Frames) ≈ $0.10, 5 s (121 Frames) ≈ $0.25 (§10-Pricing: flat 6 kostendeckend,
// loop/VACE-Präzedenz).
//
// Modell-Contract `fal-ai/seedvr/upscale/video` (fal-Doku verifiziert 2026-06-10):
//   INPUT: video_url (REQUIRED), upscale_mode (target|factor, Default factor), upscale_factor
//   (float, Default 2), target_resolution (720p|1080p|1440p|2160p), seed, noise_scale (Default
//   0.1), output_format (Default "X264 (.mp4)"), output_quality (low|medium|high|maximum,
//   Default high), output_write_mode. OUTPUT: { video: { url, content_type, file_name,
//   file_size }, seed }.
//
// D27 (generate/poll): generate() submittet NUR → Handle (status_url/response_url in raw);
// poll() ist EIN Status-GET, bei COMPLETED das verbesserte Video (URL — die Capability
// persistiert es wie ein gen-Video). Resume-fähig: ein erschöpftes Tick-Budget pollt dasselbe
// Handle weiter (kein Re-Submit, keine doppelten fal-COGS).

const FAL_QUEUE_BASE_URL = "https://queue.fal.run";
const FAL_SEEDVR_MODEL = "fal-ai/seedvr/upscale/video";
// Skalierungsfaktor-Klemme: SeedVR2 generiert Detail bei höherer Auflösung; >2 explodiert die
// per-MPx-COGS quadratisch (2× von 540p ≈ 1080p reicht für den Schärfe-Zweck).
const MAX_UPSCALE_FACTOR = 2;

/**
 * poll()-intern (Codex-P1-Analog zu Bria/BEN2/ESRGAN): ein TRANSIENTER Fehler beim Ergebnis-
 * Fetch NACH `COMPLETED` (fal-Result-GET 429/5xx) → poll() meldet `processing` (Retry) statt
 * den fertigen Job zu killen.
 */
class SeedVrResultTransientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SeedVrResultTransientError";
  }
}

export interface FalSeedVrConfig {
  apiKey?: string;
  baseUrl?: string;
  /** Modell-Override (Default `fal-ai/seedvr/upscale/video`). */
  model?: string;
}

/**
 * §7 video-enhance → SeedVR2 über fal.ai (D168, D27-Handle). `generate({videoUrl,
 * upscaleFactor?})` → submit → Handle; `poll(handle)` → Status-GET, bei COMPLETED das
 * verbesserte Video (URL) → done.
 */
export class FalSeedVrProvider implements VideoEnhanceProvider {
  readonly id = "fal-seedvr";
  readonly version = 1;
  readonly displayName = "fal.ai (SeedVR2 Video-Enhance)";

  private readonly baseUrl: string;
  private readonly model: string;

  constructor(private readonly config: FalSeedVrConfig = {}) {
    this.baseUrl = (config.baseUrl?.trim() || FAL_QUEUE_BASE_URL).replace(/\/+$/, "");
    this.model = config.model?.trim() || FAL_SEEDVR_MODEL;
  }

  private requireKey(): string {
    if (!this.config.apiKey)
      throw new ProviderUnavailableError("video-enhance", this.id, "FAL_API_KEY fehlt (Adapter nicht konfiguriert, §13).");
    return this.config.apiKey;
  }

  private headers(apiKey: string, json: boolean): Record<string, string> {
    const h: Record<string, string> = { Authorization: `Key ${apiKey}` };
    if (json) h["Content-Type"] = "application/json";
    return h;
  }

  /** fal-Queue-URLs gegen den fal-Host validieren (SSRF/Drift). */
  private requireFalUrl(url: unknown, field: string): string {
    if (typeof url !== "string" || url.length === 0) throw new Error(`fal seedvr: '${field}' fehlt.`);
    let host: string;
    try {
      host = new URL(url).origin;
    } catch {
      throw new Error(`fal seedvr: '${field}' ist keine gültige URL.`);
    }
    if (host !== new URL(this.baseUrl).origin)
      throw new Error(`fal seedvr: '${field}' (${host}) liegt nicht auf dem fal-Host (${new URL(this.baseUrl).origin}).`);
    return url;
  }

  /**
   * Ergebnis-URL gegen die fal-CDN-Hosts pinnen (Codex-P1-Analog zu Bria/BEN2/ESRGAN): die
   * Video-URL wird als video.sourceUrl durchgereicht und stromabwärts gefetcht/persistiert —
   * die Quelle MUSS fal sein (fal.media/fal.run/fal.ai inkl. Subdomains), kein beliebiger
   * Provider-Response-Host (SSRF).
   */
  private requireFalCdnUrl(url: unknown, field: string): string {
    if (typeof url !== "string" || url.length === 0) throw new Error(`fal seedvr: '${field}' fehlt.`);
    let host: string;
    try {
      host = new URL(url).hostname.toLowerCase().replace(/\.+$/, "");
    } catch {
      throw new Error(`fal seedvr: '${field}' ist keine gültige URL.`);
    }
    const allowed = ["fal.media", "fal.run", "fal.ai"].some((d) => host === d || host.endsWith(`.${d}`));
    if (!allowed) throw new Error(`fal seedvr: '${field}'-Host '${host}' liegt nicht auf einem fal-CDN-Host.`);
    return url;
  }

  /** COMPLETED → verbessertes Video (URL). Transienter Result-Flap (429/5xx) → SeedVrResultTransientError. */
  private async fetchResult(
    responseUrl: string,
    apiKey: string,
    requestId: string,
    signal?: AbortSignal,
  ): Promise<VideoEnhanceResult> {
    const resultRes = await fetch(responseUrl, { method: "GET", headers: this.headers(apiKey, false), signal });
    if (!resultRes.ok) {
      if (resultRes.status === 429 || resultRes.status >= 500)
        throw new SeedVrResultTransientError(`fal seedvr result: HTTP ${resultRes.status} (transient)`);
      const body = (await resultRes.text().catch(() => "")).slice(0, 500);
      throw new Error(`fal seedvr result: HTTP ${resultRes.status} ${resultRes.statusText}${body ? ` — ${body}` : ""}`);
    }
    const result = (await resultRes.json()) as { video?: { url?: unknown } };
    if (typeof result.video?.url !== "string" || result.video.url.length === 0)
      throw new Error("fal seedvr: COMPLETED, aber kein video.url im Ergebnis.");
    const url = this.requireFalCdnUrl(result.video.url, "video.url");
    return {
      videoUrl: url,
      provenance: { provider: this.id, modelVersion: this.model, requestId, seed: null },
    };
  }

  /** D27: NUR submitten → Handle. `opts.signal` bricht ab. */
  async generate(req: VideoEnhanceRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle> {
    const apiKey = this.requireKey();
    // Faktor defensiv klemmen (COGS sind quadratisch im Faktor); nicht-finite → Default 2.
    const requested = typeof req.upscaleFactor === "number" && Number.isFinite(req.upscaleFactor) ? req.upscaleFactor : 2;
    const factor = Math.max(1, Math.min(MAX_UPSCALE_FACTOR, requested));
    const submit = await fetch(`${this.baseUrl}/${this.model}`, {
      method: "POST",
      headers: this.headers(apiKey, true),
      body: JSON.stringify({
        video_url: req.videoUrl,
        upscale_mode: "factor",
        upscale_factor: factor,
        // H.264-MP4 in hoher Qualität: Drop-in für das gen-Video (Decode/loop/key lesen MP4);
        // Defaults für noise_scale/seed bleiben beim Modell (deterministisch genug — das
        // Ergebnis wird ohnehin als Artefakt persistiert, kein Re-Run-Drift-Risiko, D27/D58).
        output_format: "X264 (.mp4)",
        output_quality: "high",
      }),
      signal: opts?.signal,
    });
    if (!submit.ok) {
      const body = submit.status < 500 ? (await submit.text().catch(() => "")).slice(0, 500) : "";
      throw new Error(`fal seedvr submit: HTTP ${submit.status} ${submit.statusText}${body ? ` — ${body}` : ""}`);
    }
    const sub = (await submit.json()) as { request_id?: string; status_url?: unknown; response_url?: unknown };
    if (!sub.request_id) throw new Error("fal seedvr submit: keine request_id in der Antwort.");
    const statusUrl = this.requireFalUrl(sub.status_url, "status_url");
    const responseUrl = this.requireFalUrl(sub.response_url, "response_url");
    return { providerId: this.id, providerVersion: this.version, externalId: sub.request_id, raw: { statusUrl, responseUrl } };
  }

  /** D27: EIN Status-GET. IN_QUEUE→pending, IN_PROGRESS→processing, COMPLETED→Video→done. */
  async poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderVideoEnhanceJobState> {
    const apiKey = this.requireKey();
    const signal = opts?.signal;
    const raw = (handle.raw ?? {}) as { statusUrl?: unknown; responseUrl?: unknown };
    // SSRF/Drift: die URLs round-trippen als jsonb durch die DB — beim Poll erneut validieren.
    const statusUrl = this.requireFalUrl(raw.statusUrl, "status_url (handle)");
    const responseUrl = this.requireFalUrl(raw.responseUrl, "response_url (handle)");

    const statusRes = await fetch(statusUrl, { method: "GET", headers: this.headers(apiKey, false), signal });
    if (!statusRes.ok) {
      if (statusRes.status === 429 || statusRes.status >= 500) return { status: "processing" };
      const body = (await statusRes.text().catch(() => "")).slice(0, 500);
      return { status: "failed", error: `fal seedvr status: HTTP ${statusRes.status} ${statusRes.statusText}${body ? ` — ${body}` : ""}` };
    }
    const status = (await statusRes.json()) as { status?: string; error?: string };
    if (status.error) return { status: "failed", error: `fal seedvr: ${status.error}` };
    switch (status.status) {
      case "COMPLETED":
        try {
          return { status: "done", output: await this.fetchResult(responseUrl, apiKey, handle.externalId, signal) };
        } catch (e) {
          if (e instanceof SeedVrResultTransientError) return { status: "processing" };
          throw e;
        }
      case "IN_QUEUE":
        return { status: "pending" };
      case "IN_PROGRESS":
        return { status: "processing" };
      default:
        return { status: "failed", error: `fal seedvr: unerwarteter Status '${status.status ?? "—"}' (terminal ohne error-Feld?).` };
    }
  }
}
